import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import Pagination from '../../components/common/Pagination';
import styles from './Reporters.module.css';

function parseFieldErrors(errors) {
  if (!errors || typeof errors !== 'object') return {};
  const result = {};
  Object.entries(errors).forEach(([key, val]) => {
    result[key] = Array.isArray(val) ? val[0] : String(val);
  });
  return result;
}

function FieldInput({ label, error, children }) {
  return (
    <label className={styles.label}>
      {label}
      {children}
      {error && <span className={styles.fieldError}>{error}</span>}
    </label>
  );
}

function ReporterForm({ businesses, reporter, onSave, onCancel }) {
  const [email, setEmail] = useState(reporter?.email || '');
  const [firstName, setFirstName] = useState(reporter?.first_name || '');
  const [lastName, setLastName] = useState(reporter?.last_name || '');
  const [businessId, setBusinessId] = useState(reporter?.assigned_business || '');
  const [templateId, setTemplateId] = useState(reporter?.assigned_template || '');
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!businessId) {
      setTemplates([]);
      setTemplateId('');
      return;
    }
    setLoadingTemplates(true);
    api.get(`/businesses/${businessId}/templates/`)
      .then(res => {
        if (res.data.success) {
          const active = (res.data.data || []).filter(t => t.is_active);
          setTemplates(active);
          if (templateId && !active.find(t => t.id === templateId)) {
            setTemplateId('');
          }
        }
      })
      .catch(() => setTemplates([]))
      .finally(() => setLoadingTemplates(false));
  }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  function clearFieldError(field) {
    setFieldErrors(prev => ({ ...prev, [field]: '' }));
  }

  function handleBusinessChange(e) {
    setBusinessId(e.target.value);
    setTemplateId('');
    clearFieldError('assigned_business');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setFieldErrors({});
    try {
      const payload = {
        email,
        first_name: firstName,
        last_name: lastName,
        assigned_business: businessId,
        assigned_template: templateId || null,
      };
      if (reporter) {
        await api.put(`/reporters/${reporter.id}/`, payload);
      } else {
        await api.post('/reporters/', payload);
      }
      onSave();
    } catch (err) {
      const data = err.response?.data || {};
      const fe = parseFieldErrors(data.errors);
      if (Object.keys(fe).length > 0) {
        setFieldErrors(fe);
      } else {
        setError(data.message || 'Failed to save reporter.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <h2>{reporter ? 'Edit Reporter' : 'Add Reporter'}</h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}
          <FieldInput label="Email" error={fieldErrors.email}>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); clearFieldError('email'); }}
              className={`${styles.input} ${fieldErrors.email ? styles.inputError : ''}`}
              required
              disabled={!!reporter}
            />
          </FieldInput>
          <div className={styles.row}>
            <FieldInput label="First Name" error={fieldErrors.first_name}>
              <input
                type="text"
                value={firstName}
                onChange={e => { setFirstName(e.target.value); clearFieldError('first_name'); }}
                className={`${styles.input} ${fieldErrors.first_name ? styles.inputError : ''}`}
                required
              />
            </FieldInput>
            <FieldInput label="Last Name" error={fieldErrors.last_name}>
              <input
                type="text"
                value={lastName}
                onChange={e => { setLastName(e.target.value); clearFieldError('last_name'); }}
                className={`${styles.input} ${fieldErrors.last_name ? styles.inputError : ''}`}
                required
              />
            </FieldInput>
          </div>
          <FieldInput label="Assigned Business" error={fieldErrors.assigned_business}>
            <select
              value={businessId}
              onChange={handleBusinessChange}
              className={`${styles.input} ${fieldErrors.assigned_business ? styles.inputError : ''}`}
              required
            >
              <option value="">Select a business...</option>
              {businesses.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </FieldInput>
          <FieldInput label="Assigned Template" error={fieldErrors.assigned_template}>
            <select
              value={templateId}
              onChange={e => { setTemplateId(e.target.value); clearFieldError('assigned_template'); }}
              className={`${styles.input} ${fieldErrors.assigned_template ? styles.inputError : ''}`}
              disabled={!businessId || loadingTemplates}
            >
              <option value="">
                {!businessId
                  ? 'Select a business first'
                  : loadingTemplates
                    ? 'Loading…'
                    : templates.length === 0
                      ? 'No active templates'
                      : 'Select a template...'}
              </option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name || `Version ${t.version}`}</option>
              ))}
            </select>
          </FieldInput>
          <div className={styles.formActions}>
            <button type="button" onClick={onCancel} className={styles.btnSecondary}>Cancel</button>
            <button type="submit" disabled={saving} className={styles.btnPrimary}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Reporters() {
  const [reporters, setReporters] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editReporter, setEditReporter] = useState(null);
  const [actionError, setActionError] = useState('');
  const [filterBiz, setFilterBiz] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);

  const fetchData = useCallback(async () => {
    try {
      const [repRes, bizRes] = await Promise.all([
        api.get('/reporters/'),
        api.get('/businesses/'),
      ]);
      if (repRes.data.success) setReporters(repRes.data.data || []);
      if (bizRes.data.success) setBusinesses(bizRes.data.data || []);
    } catch { /* empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleAdd() {
    setEditReporter(null);
    setShowForm(true);
  }

  function handleEdit(reporter) {
    setEditReporter(reporter);
    setShowForm(true);
  }

  async function handleToggleActive(reporter, activate) {
    setActionError('');
    // Optimistic update — change state in place so list order never shifts
    setReporters(prev => prev.map(r => r.id === reporter.id ? { ...r, is_active: activate } : r));
    try {
      await api.put(`/reporters/${reporter.id}/`, { is_active: activate });
    } catch (err) {
      // Revert on failure
      setReporters(prev => prev.map(r => r.id === reporter.id ? { ...r, is_active: reporter.is_active } : r));
      setActionError(err.response?.data?.message || 'Failed to update reporter.');
    }
  }

  async function handleDelete(reporter) {
    if (!window.confirm(`Permanently delete reporter "${reporter.email}"? They will no longer be able to log in.`)) return;
    setActionError('');
    try {
      await api.delete(`/reporters/${reporter.id}/`);
      fetchData();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to delete reporter.');
    }
  }

  function handleSaved() {
    setShowForm(false);
    setEditReporter(null);
    fetchData();
  }

  function getBusinessName(id) {
    const biz = businesses.find(b => b.id === id);
    return biz ? biz.name : 'Unknown';
  }

  const filteredReporters = reporters.filter(r => {
    if (filterBiz && r.assigned_business !== filterBiz) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = `${r.first_name} ${r.last_name}`.toLowerCase();
      if (!name.includes(q) && !r.email.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const pagedReporters = filteredReporters.slice((page - 1) * perPage, page * perPage);

  return (
    <div>
      <PageHeader
        title="Reporters"
        subtitle="Manage people who submit daily entries"
        actions={
          <div className={styles.headerActions}>
            <div className={styles.searchWrap}>
              <svg className={styles.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search name or email..."
                className={styles.searchInput}
              />
            </div>
            <select
              value={filterBiz}
              onChange={e => { setFilterBiz(e.target.value); setPage(1); }}
              className={styles.filterSelect}
            >
              <option value="">All Businesses</option>
              {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <button onClick={handleAdd} className={styles.btnPrimary}>+ Add Reporter</button>
          </div>
        }
      />

      {actionError && (
        <div className={styles.error} style={{ marginBottom: 16 }}>{actionError}</div>
      )}

      {loading ? (
        <p className={styles.loadingText}>Loading...</p>
      ) : filteredReporters.length === 0 ? (
        <div className={styles.emptyState}>
          <h3>{reporters.length === 0 ? 'No reporters yet' : 'No reporters match your filters'}</h3>
          <p>{reporters.length === 0 ? 'Add reporters to start collecting daily entries for your businesses.' : 'Try adjusting the search or business filter.'}</p>
          {reporters.length === 0 && <button onClick={handleAdd} className={styles.btnPrimary}>+ Add Reporter</button>}
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.slNoTh}>Sl. No</th>
                <th>Name</th>
                <th>Email</th>
                <th>Assigned Business</th>
                <th>Template</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedReporters.map((r, idx) => (
                <tr key={r.id}>
                  <td className={styles.slNoTd}>{(page - 1) * perPage + idx + 1}</td>
                  <td className={styles.nameCell}>{r.first_name} {r.last_name}</td>
                  <td>{r.email}</td>
                  <td>{r.assigned_business ? getBusinessName(r.assigned_business) : '-'}</td>
                  <td>{r.assigned_template_name || (r.assigned_template ? '—' : '—')}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${r.is_active !== false ? styles.active : styles.inactive}`}>
                      {r.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      {/* Edit */}
                      <button
                        onClick={() => handleEdit(r)}
                        className={`${styles.iconBtn} ${styles.iconBtnEdit}`}
                        title="Edit"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>

                      {/* Deactivate / Activate */}
                      {r.is_active !== false ? (
                        <button
                          onClick={() => handleToggleActive(r, false)}
                          className={`${styles.iconBtn} ${styles.iconBtnDeactivate}`}
                          title="Deactivate"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="4" width="4" height="16" rx="1"/>
                            <rect x="14" y="4" width="4" height="16" rx="1"/>
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleToggleActive(r, true)}
                          className={`${styles.iconBtn} ${styles.iconBtnActivate}`}
                          title="Activate"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="5,3 19,12 5,21"/>
                          </svg>
                        </button>
                      )}

                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(r)}
                        className={`${styles.iconBtn} ${styles.iconBtnDelete}`}
                        title="Delete"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6"/><path d="M14 11v6"/>
                          <path d="M9 6V4h6v2"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            total={filteredReporters.length}
            page={page}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={n => { setPerPage(n); setPage(1); }}
          />
        </div>
      )}

      {showForm && (
        <ReporterForm
          businesses={businesses}
          reporter={editReporter}
          onSave={handleSaved}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

export default Reporters;
